import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button, Flex, Icon, Spinner, Text, useColorModeValue } from "@chakra-ui/react";
import { MdHistory } from "react-icons/md";
import Card from "horizon/components/card/Card";
import { fetchAuditLog } from "./auditLogApi";
import { RETENTION_NOTICE } from "./auditLogConstants";
import AuditLogFilters from "./AuditLogFilters";
import AuditLogTable from "./AuditLogTable";
import AuditLogDetailDrawer from "./AuditLogDetailDrawer";

const INITIAL_FILTERS = {
  action: "", admin_id: "", target_user_id: "", start_date: "", end_date: "", page: 1, per_page: 50,
};

/**
 * Loads a page of audit entries into state, surfacing a toast on error.
 * @param {Object} filters - Active filters incl. page/per_page
 * @param {Function} setItems - Setter for the entries list
 * @param {Function} setInfo - Setter for { total, pages }
 * @param {Function} setLoading - Setter for the loading flag
 * @returns {Promise<void>}
 */
async function loadAuditLog(filters, setItems, setInfo, setLoading) {
  setLoading(true);
  try {
    const data = await fetchAuditLog(filters);
    setItems(data?.items || []);
    setInfo({ total: data?.total || 0, pages: data?.pages || 0 });
  } catch (error) {
    toast.error(typeof error?.response?.data === "string" ? error.response.data : "Chargement du journal impossible");
    setItems([]);
    setInfo({ total: 0, pages: 0 });
  } finally {
    setLoading(false);
  }
}

/**
 * Renders the page header card with title and retention notice.
 * @returns {React.ReactElement}
 */
function PageHeader() {
  const titleColor = useColorModeValue("secondaryGray.900", "white");
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const badgeBg = useColorModeValue("secondaryGray.300", "navy.700");
  return (
    <Card>
      <Flex align="center" gap="14px">
        <Flex align="center" justify="center" w="44px" h="44px" borderRadius="12px" bg={badgeBg}>
          <Icon as={MdHistory} w="22px" h="22px" color="brand.500" />
        </Flex>
        <Flex direction="column" minW="0">
          <Text fontSize="lg" fontWeight="700" color={titleColor}>Journal d'audit</Text>
          <Text fontSize="sm" color={subColor}>{RETENTION_NOTICE}</Text>
        </Flex>
      </Flex>
    </Card>
  );
}

/**
 * Pagination footer with previous/next controls.
 * @param {Object} props - page, pages, total, onPrev, onNext
 * @returns {React.ReactElement}
 */
function Pagination({ page, pages, total, onPrev, onNext }) {
  const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Flex justify="space-between" align="center" mt="4px">
      <Text fontSize="sm" color={color}>{total} entrée(s) — page {page} / {pages || 1}</Text>
      <Flex gap="8px">
        <Button size="sm" variant="outline" colorScheme="brand" isDisabled={page <= 1} onClick={onPrev}>Précédent</Button>
        <Button size="sm" variant="outline" colorScheme="brand" isDisabled={page >= pages} onClick={onNext}>Suivant</Button>
      </Flex>
    </Flex>
  );
}

/**
 * Super Admin audit log viewer: filters, paginated table and a detail drawer
 * exposing payload_before / payload_after.
 * @returns {React.ReactElement}
 */
function AuditLog() {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [items, setItems] = useState([]);
  const [info, setInfo] = useState({ total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { loadAuditLog(filters, setItems, setInfo, setLoading); }, [filters]);

  return (
    <Flex direction="column" gap="16px">
      <PageHeader />
      <AuditLogFilters onApply={(draft) => setFilters({ ...filters, ...draft, page: 1 })} />
      {loading
        ? <Flex justify="center" py="40px"><Spinner color="brand.500" /></Flex>
        : <AuditLogTable items={items} onSelect={setSelected} />}
      <Pagination
        page={filters.page} pages={info.pages} total={info.total}
        onPrev={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })}
        onNext={() => setFilters({ ...filters, page: filters.page + 1 })}
      />
      <AuditLogDetailDrawer row={selected} onClose={() => setSelected(null)} />
    </Flex>
  );
}

export default AuditLog;
